import { CSSProperties } from 'react';

export const colours = {
  white: '#FFFFFF',
  errorTextRed: '#F7493D',
  errorBorderRed: '#FF7D74',
  removeTextRed: '#EB1203',
  textBlack: '#4A4D59',
  darkerTextBlack: '#20242C',
  placeholderGrey: '#9B9DAB',
  borderGrey: '#ABAEB4',
  chipBorderGrey: '#D7D8DC',
  selectedBorderBlue: '#339BFA',
  infoBlue: '#0554B4',
  loadingBlue: '#648BBB',
  disabledGrey: '#ECEDF4',
};

const border: CSSProperties = {
  borderRadius: '4px',
  border: `1.5px solid ${colours.borderGrey}`,
};

const errorBorder: CSSProperties = {
  border: `1.5px solid ${colours.errorBorderRed}`,
};

export const LabelAndTooltipStyles = {
  labelAndTooltipContainer: {
    display: 'flex',
    minHeight: '24px',
    alignItems: 'flex-start',
  },
  label: {
    color: colours.textBlack,
    fontFamily: 'DM Sans',
    fontSize: '14px',
    fontWeight: '400',
  },
  tooltip: { marginLeft: '4px' },
};

export const InfoAndErrorStyles = {
  errorText: {
    marginLeft: '4px',
    color: colours.errorTextRed,
    fontSize: '10px',
    fontWeight: '600',
    lineHeight: '150%',
  },
  errorIcon: { minHeight: '12px', minWidth: '12px' },
  infoContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'start',
    marginTop: '4px',
  },
  info: {
    marginLeft: '4px',
    color: colours.infoBlue,
    fontSize: '10px',
    fontWeight: '600',
    lineHeight: '150%',
  },
};

export const commonFormFieldStyles = {
  height: '40px',
  width: '100%',
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#fff',
  color: colours.infoBlue,
  ...border,
};

export const checkboxStyles = {
  container: {
    display: 'flex',
    alignItems: 'start',
    gap: '8px',
    width: 'fit-content',
  } as CSSProperties,
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  } as CSSProperties,
  label: {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '150%',
    width: 'fit-content',
  } as CSSProperties,
};

export const inputStyles = {
  input: {
    ...commonFormFieldStyles,
  },
  error: { ...errorBorder },
};

export const datePickerStyles = {
  datePicker: {
    ...commonFormFieldStyles,
    WebkitAppearance: 'none',
    fontWeight: '500',
  } as CSSProperties,
  input: {
    position: 'relative',
  } as CSSProperties,
  icon: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    pointerEvents: 'none',
  } as CSSProperties,
  error: { ...errorBorder },
  placeholder: {
    color: colours.placeholderGrey,
    fontSize: '14px',
    fontWeight: '500',
    lineHeight: '166%',
  } as CSSProperties,
};

export const selectStyles = {
  select: {
    ...commonFormFieldStyles,
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    appearance: 'none',
    backgroundImage:
      'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="2" stroke-linecap="butt" stroke-linejoin="miter"><path d="M6 9l6 6 6-6"/></svg>\')',
    backgroundPosition: 'right 10px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '20px',
    paddingRight: '32px',
    textOverflow: 'ellipsis',
  } as CSSProperties,
  placeholder: { color: colours.placeholderGrey } as CSSProperties,
  error: { ...errorBorder },
};

export const dateSelectStyles = {
  selectsContainer: {
    display: 'flex',
    gap: '8px',
  } as CSSProperties,
  error: { ...errorBorder },
};

export const selectWithLogosStyles = {
  selectWithLogo: {
    ...commonFormFieldStyles,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as CSSProperties,
  option: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  } as CSSProperties,
  logoContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '80px',
  } as CSSProperties,
  logo: {
    maxHeight: '20px',
    maxWidth: '64px',
    objectFit: 'contain',
  } as CSSProperties,
};

const buttonStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '40px',
  padding: '0 12px',
  borderRadius: '4px',
  border: `1px solid ${colours.infoBlue}`,
  cursor: 'pointer',
};

const buttonText = {
  fontFamily: 'DM Sans',
  fontSize: '14px',
  fontWeight: '700',
  lineHeight: '116%',
  letterSpacing: '0.14px',
  padding: '0 4px',
};

export const prevButtonStyles = {
  button: {
    ...buttonStyles,
    background: colours.white,
  },
  text: {
    ...buttonText,
    color: colours.infoBlue,
  },
};

export const nextButtonStyles = {
  button: {
    ...buttonStyles,
    justifyContent: 'center',
    background: colours.infoBlue,
  },
  text: {
    ...buttonText,
    color: colours.white,
  },
  disabledButton: {
    background: colours.disabledGrey,
    border: 'none',
    cursor: 'unset',
  },
  disabledText: {
    color: colours.placeholderGrey,
  },
  loadingButton: {
    background: colours.loadingBlue,
    border: 'none',
    cursor: 'unset',
  } as CSSProperties,
};

export const radioStyles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: '1',
    width: '100%',
  } as CSSProperties,
  options: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  } as CSSProperties,
  label: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    padding: '4px 12px',
    alignItems: 'center',
    height: '40px',
    width: '100%',
    cursor: 'pointer',
    background: colours.white,
    ...border,
  } as CSSProperties,
  icon: {
    position: 'absolute',
    left: '8px',
    display: 'flex',
    alignItems: 'center',
  } as CSSProperties,
  selected: {
    border: `2px solid ${colours.selectedBorderBlue}`,
    borderRadius: '4px',
  } as CSSProperties,
  selectedCheckIcon: {
    position: 'absolute',
    top: '2px',
    right: '2px',
  } as CSSProperties,
};

export const policyAddOnStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '8px 0',
  } as CSSProperties,
  titleAndPrice: {
    width: '100%',
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  } as CSSProperties,
  title: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'DM Sans',
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '116%',
    minHeight: '20px',
  },
  priceAndAddButton: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '32px',
  } as CSSProperties,
  priceAndFrequency: {
    display: 'flex',
    justifyContent: 'start',
    gap: '4px',
  },
  price: {
    fontFamily: 'DM Sans',
    fontSize: '14px',
    fontWeight: '700',
    lineHeight: '150%',
    color: colours.infoBlue,
  } as CSSProperties,
  frequency: {
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '166%',
  } as CSSProperties,
  addButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '32px',
    padding: '0 8px',
    borderRadius: '4px',
    border: `1px solid ${colours.infoBlue}`,
    cursor: 'pointer',
  } as CSSProperties,
  addText: {
    color: colours.infoBlue,
    fontFamily: 'DM Sans',
    fontSize: '10px',
    fontWeight: '700',
    lineHeight: '116%',
    letterSpacing: '0.2px',
    padding: '0 4px',
  } as CSSProperties,
  select: {
    display: 'flex',
    justifyContent: 'end',
  } as CSSProperties,
  right: {
    justifyContent: 'end',
  },
};

export const removeButtonStyles = {
  removeButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '20px',
    gap: '2px',
    cursor: 'pointer',
  } as CSSProperties,
  removeText: {
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '150%',
    color: colours.removeTextRed,
  } as CSSProperties,
};

export const policyAddOnLargeStyles = {
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '8px 0',
  } as CSSProperties,
  titleAndPrice: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '47px',
    width: '100%',
  } as CSSProperties,
  titleAndPriceAdded: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '47px',
    width: '100%',
  } as CSSProperties,
  iconAndTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
  } as CSSProperties,
  priceAndFrequency: {
    display: 'flex',
    gap: '4px',
  },
  pricePrefixLabel: {
    display: 'flex',
    alignItems: 'center',
    color: colours.infoBlue,
    fontSize: '14px',
    fontWeight: '600',
    lineHeight: '166%',
  } as CSSProperties,
  price: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'DM Sans',
    fontSize: '14px',
    fontWeight: '700',
    lineHeight: '150%',
    color: colours.infoBlue,
  } as CSSProperties,
  frequency: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '166%',
  } as CSSProperties,
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: 'DM Sans',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '116%',
    minHeight: '20px',
  },
  added: {
    justifyContent: 'center',
  } as CSSProperties,
  tooltip: {
    marginLeft: '4px',
  } as CSSProperties,
  checkbox: {
    ...checkboxStyles.checkbox,
  },
  select: {
    width: '120px',
  } as CSSProperties,
  selectMobile: {
    width: '100%',
    marginTop: '8px',
    marginBottom: '4px',
  } as CSSProperties,
  ruby: {
    display: 'ruby',
  } as CSSProperties,
  hidden: {
    display: 'none',
  } as CSSProperties,
};

export const navigationStyles = {
  container: {
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: '32px',
  } as CSSProperties,
  mobileContainer: {
    flexDirection: 'column',
    gap: '16px',
  } as CSSProperties,
  moveToLeft: {
    justifyContent: 'start',
  } as CSSProperties,
};

export const counterChipsStyles = {
  chipContainer: {
    display: 'flex',
    alignContent: 'center',
    gap: '8px',
  } as CSSProperties,
  chip: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '40px',
    width: '40px',
    minHeight: '40px',
    minWidth: '40px',
    border: `1px solid ${colours.chipBorderGrey}`,
    borderRadius: '20px',
    cursor: 'pointer',
  } as CSSProperties,
  firstChip: {
    width: '80px',
    minWidth: '80px',
  } as CSSProperties,
  lastChip: {
    width: '48px',
    minWidth: '48px',
  } as CSSProperties,
  chipSelected: {
    color: colours.infoBlue,
    border: `1.5px solid ${colours.selectedBorderBlue}`,
  } as CSSProperties,
};

export const TextMultiSelectStyles = {
  container: {
    marginBottom: '40px',
  } as CSSProperties,
  optionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
  } as CSSProperties,
  label: {
    minHeight: '20px',
    color: '#25272C',
    fontSize: '16px',
    fontWeight: '600',
    lineHeight: '116%',
    marginBottom: '16px',
  } as CSSProperties,
  option: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '28px',
    padding: '0 8px',
    border: `1px solid ${colours.chipBorderGrey}`,
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: '600',
    lineHeight: '150%',
    color: colours.textBlack,
    cursor: 'pointer',
  } as CSSProperties,
  selected: {
    color: colours.infoBlue,
    border: `1.5px solid ${colours.selectedBorderBlue}`,
  } as CSSProperties,
};

export const infoBoxStyles = {
  display: "flex",
  padding: "8px",
  alignItems: "center",
  border: "1px solid #D5E5F8",
  background: "#EBF3FD",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "166%",
  marginBottom: "24px"
} as CSSProperties


